import { Suspense, useEffect, useState } from 'react';
import { createBrowserRouter, RouterProvider, Navigate, useParams } from 'react-router-dom';
import { APP_FEATURES, APP_ROUTES, SCREEN_MODE } from '../constants';
import Protected from '../utility/Protected';
import MainLayout from './MainLayout';
import PublicLayout from './PublicLayout';
import ChromelessLayout from './ChromelessLayout';
import SessionTimeout from '../components/sessionTimeOut/SessionTimeout';
import SubscriptionGuard from '../guards/SubscriptionGuard';
import ETLGuard from '../guards/ETLGuard';
import RouteErrorBoundary from '../components/ErrorBoundary/RouteErrorBoundary';
import { lazyRetry } from '../utility/lazyRetry';
import SubscriptionFeatureGuard from '../components/SubscriptionFeatureGuard';
// Lazy load all page components for code splitting
// Public Pages (loaded together in public bundle)
const Register = lazyRetry(() => import('../pages/organizationalProfile/Register'));
const AdministrationDetails = lazyRetry(
  () => import('../pages/organizationalProfile/AdministrationDetails'),
);
const Onboarding = lazyRetry(() => import('../pages/organizationalProfile/Onboarding'));
const DemoSchedule = lazyRetry(() => import('../pages/organizationalProfile/DemoSchedule'));
const Confirmation = lazyRetry(() => import('../pages/organizationalProfile/Confirmation'));
const Login = lazyRetry(() => import('../pages').then((m) => ({ default: m.Login })));
const ForgetPassword = lazyRetry(() =>
  import('../pages').then((m) => ({ default: m.ForgetPassword })),
);
const OtpSelectionForget = lazyRetry(() =>
  import('../pages').then((m) => ({ default: m.OtpSelectionForget })),
);
const NewCredential = lazyRetry(() =>
  import('../pages').then((m) => ({ default: m.NewCredential })),
);
const EnterOtp = lazyRetry(() => import('../pages').then((m) => ({ default: m.EnterOtp })));
const PasswordUpdated = lazyRetry(() =>
  import('../pages').then((m) => ({ default: m.PasswordUpdated })),
);
const InvalidTokenPage = lazyRetry(() =>
  import('../pages').then((m) => ({ default: m.InvalidTokenPage })),
);
const OnboardingCompletedPage = lazyRetry(() =>
  import('../pages').then((m) => ({ default: m.OnboardingCompletedPage })),
);
const EmailLinkPage = lazyRetry(() => import('../pages/email-link'));
const UnauthorizedPage = lazyRetry(() =>
  import('../pages').then((m) => ({ default: m.UnauthorizedPage })),
);
const FirstChangePassword = lazyRetry(() => import('../pages/FirstChangePassword'));
const OrganizationSelection = lazyRetry(() => import('../pages/organizationSelection'));

// Authenticated-only (role-based, NOT privilege-gated) — common role-neutral landing page
const Home = lazyRetry(() => import('../pages/Home'));

// Protected Pages - Student Management
const Student = lazyRetry(() => import('../pages').then((m) => ({ default: m.Student })));
const Profile = lazyRetry(() => import('../pages').then((m) => ({ default: m.Profile })));
const StudentManagementScreen = lazyRetry(() =>
  import('../pages').then((m) => ({ default: m.StudentManagementScreen })),
);
const ViewEditAdminStudentScreen = lazyRetry(
  () => import('../pages/classveew-admin/student-management/view-edit-screen'),
);

// Protected Pages - Teacher Management
const Teacher = lazyRetry(() => import('../pages').then((m) => ({ default: m.Teacher })));
const CreateTeacher = lazyRetry(() => import('../pages/teacherManagement/create'));
const TeacherProfile = lazyRetry(() => import('../pages/teacherManagement/profile'));

/**
 * /teacher-management/edit/:id -> /teacher-management/profile/:id
 *
 * The teacher screen was split into a create form and a tabbed profile
 * (CVA-1266); editing is now the profile's first tab. Anything holding the old
 * link — a bookmark, an email, a stale tab — lands on the right screen instead
 * of a 404.
 */
const LegacyEditTeacherRedirect = () => {
  const { id } = useParams<{ id: string }>();
  return (
    <Navigate
      to={id ? `${APP_ROUTES.TEACHER_PROFILE}/${id}` : APP_ROUTES.TEACHER_MANAGEMENT}
      replace
    />
  );
};
const TeacherManagementScreen = lazyRetry(() =>
  import('../pages').then((m) => ({ default: m.TeacherManagementScreen })),
);
const ViewEditAdminTeacherScreen = lazyRetry(
  () => import('../pages/classveew-admin/teacher-management/view-edit-screen'),
);
const GuardianManagementScreen = lazyRetry(
  () => import('../pages/classveew-admin/admin-guardians'),
);
const ViewEditAdminGuardianScreen = lazyRetry(
  () => import('../pages/classveew-admin/admin-guardians/view-edit-screen'),
);
const AdminNewsFeedScreen = lazyRetry(() => import('../pages/classveew-admin/news-feed'));
// CVA-4718: org-scoped News Feed tab — distinct from the Classveew Admin
// screen above, which stays on ADMIN_NEWS_FEED/CLASSVEEW_POST_MANAGEMENT.
const NewsFeedPage = lazyRetry(() => import('../pages/news-feed'));
const TeacherAttendance = lazyRetry(() => import('../pages/teacherAttendance'));
const StaffLeave = lazyRetry(() => import('../pages/staffLeave'));
const TeacherAssign = lazyRetry(() => import('../pages/Classroom_assign'));
const AwardManagementScreen = lazyRetry(() => import('../pages/classveew-admin/award-management'));
const DiscountLedgerScreen = lazyRetry(() => import('../pages/classveew-admin/discount-ledger'));

// Protected Pages - Attendance & Time
const Attendance = lazyRetry(() => import('../pages').then((m) => ({ default: m.Attendance })));
const AttendanceCutoffTime = lazyRetry(() => import('../pages/timeSetting'));

// Protected Pages - Events
const Events = lazyRetry(() => import('../pages/events'));
const AddEditEvent = lazyRetry(() => import('../pages/addEditEvent/AddEditEvent'));
const ViewEvents = lazyRetry(() => import('../pages/viewEvents'));

// Protected Pages - Fee Management
const FeeTemplate = lazyRetry(() => import('../pages/feeTemplate'));
const StudentFees = lazyRetry(() => import('../pages/studentFees'));
const SessionHistory = lazyRetry(() =>
  import('../pages').then((m) => ({ default: m.SessionHistory })),
);

// Protected Pages - Book Management
const BookManagement = lazyRetry(() => import('../pages/libraryManagement'));
const BookCirculation = lazyRetry(() => import('../pages/bookCirculation'));

// Protected Pages - Classroom & Grade
const ClassRoom = lazyRetry(() => import('../pages').then((m) => ({ default: m.ClassRoom })));
const Grade = lazyRetry(() => import('../pages').then((m) => ({ default: m.Grade })));

// Protected Pages - Academic Management
const SyllabusManagement = lazyRetry(() => import('../pages/syllabus'));
const CreateSyllabusForm = lazyRetry(() => import('../pages/syllabus/newSyllabus'));
const CurriculumScreen = lazyRetry(() =>
  import('../pages/curriculumManagement').then((m) => ({ default: m.CurriculumScreen })),
);
const AcademicYearPage = lazyRetry(() =>
  import('../pages/academicYear').then((m) => ({ default: m.AcademicYearScreen })),
);
const Subjects = lazyRetry(() => import('../pages').then((m) => ({ default: m.Subjects })));
const SubjectAssign = lazyRetry(() =>
  import('../pages').then((m) => ({ default: m.SubjectAssign })),
);

// Protected Pages - Exam & Score
const ScoreCardPage = lazyRetry(() => import('../pages/scoreCard'));
const ViewExamSubjects = lazyRetry(() => import('../pages/viewExam'));
const SubjectMarking = lazyRetry(() => import('../pages/examMarking/SubjectMarking'));
// CVA-4197 plan 0011 slice 4 — Flow A canvas (per-student, per-question grading).
const MarkExamCanvas = lazyRetry(() => import('../pages/examMarking/MarkExamCanvas'));
const ViewExamPaper = lazyRetry(() => import('../pages/viewExamPaper'));
const HomeworkDashboard = lazyRetry(() => import('../pages/homework'));
const CreateHomework = lazyRetry(() => import('../pages/homework/CreateHomework'));
const EditHomework = lazyRetry(() => import('../pages/homework/EditHomework'));
const ViewHomework = lazyRetry(() => import('../pages/homework/ViewHomework'));
const GradingSubmissions = lazyRetry(() => import('../pages/homework/GradingSubmissions'));

// Protected Pages - Parent (guardian) flow
const ParentHomeworkDashboard = lazyRetry(() => import('../pages/parent/homework'));
const ParentViewHomework = lazyRetry(() => import('../pages/parent/homework/ViewHomework'));
const ParentTimetablePage = lazyRetry(() => import('../pages/parent/timetable'));
const ParentEventsPage = lazyRetry(() => import('../pages/parent/events'));
const ParentSubjectsPage = lazyRetry(() => import('../pages/parent/subjects'));
const StudentExamDashboard = lazyRetry(() => import('../pages/studentPortal/exam'));
const StudentViewExam = lazyRetry(() => import('../pages/studentPortal/exam/ViewExam'));
const StudentReportCardPage = lazyRetry(() => import('../pages/studentPortal/exam/ReportCard'));
const StudentExamAttemptPage = lazyRetry(() => import('../pages/studentPortal/examAttempt'));

// Single-exam report card (staff view, opened from the exam student list)
const ExamReportCard = lazyRetry(() => import('../pages/reportCard'));

// Protected Pages - Timetable
const TimeTable = lazyRetry(() => import('../pages/timeTable'));
const SetTimeTable = lazyRetry(() => import('../pages').then((m) => ({ default: m.SetTimeTable })));

// Protected Pages - User & Role Management
const UserPolicy = lazyRetry(() => import('../pages/userPolicies'));
const RoleManager = lazyRetry(() => import('../pages/addUserType'));
const AssignUserRole = lazyRetry(() => import('../pages/assignUserRole'));
const GuardianManagementPage = lazyRetry(() => import('../pages/guardianManagement'));
const GuardianOnboardingPage = lazyRetry(() => import('../pages/guardian-onboarding'));
const OrganizationAdminManagementPage = lazyRetry(
  () => import('../pages/organizationAdminManagement'),
);

// Protected Pages - Organization Management
const ViewAllOrganizationsScreen = lazyRetry(() =>
  import('../pages').then((m) => ({ default: m.ViewAllOrganizationsScreen })),
);
const RequestOrganizationScreen = lazyRetry(() =>
  import('../pages').then((m) => ({ default: m.RequestOrganizationScreen })),
);
const CreateOrganizationScreen = lazyRetry(() =>
  import('../pages').then((m) => ({ default: m.CreateOrganizationScreen })),
);
const ManageOrganizationScreen = lazyRetry(() =>
  import('../pages').then((m) => ({ default: m.ManageOrganizationScreen })),
);
const InstituteDetails = lazyRetry(() => import('../pages/organizationalProfile/institue'));
const MyProfile = lazyRetry(() => import('../pages/myProfile'));
const TwoFactorVerification = lazyRetry(() => import('../pages/twoFactorVerification'));

// Protected Pages - Other
const Dashboard = lazyRetry(() => import('../pages').then((m) => ({ default: m.Dashboard })));
const ETLDashboard = lazyRetry(() => import('../pages/etl-dashboard'));
const EtlQuickSetup = lazyRetry(() => import('../pages/etl-quick-setup'));
const EtlQuickSetupReview = lazyRetry(() => import('../pages/etl-quick-setup/review'));
const ChangePassword = lazyRetry(() => import('../pages/changePassword'));
const ReEnrollmentManagement = lazyRetry(() =>
  import('../pages').then((m) => ({ default: m.ReEnrollmentManagement })),
);
const ReEnrollmentStudentList = lazyRetry(
  () => import('../pages/reEnrollmentManagement/ReEnrollmentStudentList'),
);
const BillingAndPlans = lazyRetry(() => import('../pages/billing'));
const ForbiddenPage = lazyRetry(() =>
  import('../pages').then((m) => ({ default: m.ForbiddenPage })),
);
const NotFoundPage = lazyRetry(() => import('../pages').then((m) => ({ default: m.NotFoundPage })));

// Protected Pages - Reports
const Reports = lazyRetry(() => import('../pages').then((m) => ({ default: m.Reports })));
const FinanceReport = lazyRetry(() =>
  import('../pages').then((m) => ({ default: m.FinanceReport })),
);
const StudentReport = lazyRetry(() =>
  import('../pages').then((m) => ({ default: m.StudentReport })),
);
const TeacherReport = lazyRetry(() =>
  import('../pages').then((m) => ({ default: m.TeacherReport })),
);
const StudentAttendanceReport = lazyRetry(() =>
  import('../pages/reports/StudentAttendanceReport').then((m) => ({ default: m.default })),
);

// Guards (direct import - not lazy loaded since they're wrappers)
import SessionGuard from '../guards/SessionGuard';

// Suspense wrapper component for lazy-loaded routes
import SuspenseLoader from '../components/Loading/SuspenseLoader';
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- Classveew AI route is hidden on UAT; kept for re-enable
const ClassveewAIPage = lazyRetry(() => import('../pages/classveew-ai'));

const ReadyNotifier: React.FC<{ onReady: () => void; children: React.ReactNode }> = ({
  onReady,
  children,
}) => {
  useEffect(() => {
    onReady();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return <>{children}</>;
};

// Suspense wrapper component for lazy-loaded routes
// Shows the SuspenseLoader fallback while a lazy route loads, and keeps
// an overlay visible for at least REACT_APP_SUSPENSE_MIN_MS milliseconds
// so the animation can be observed during fast loads in development.
const LazyRoute = ({ children }: { children: React.ReactNode }) => {
  const minMs = Number(1000);
  const [ready, setReady] = useState(false);
  const [minElapsed, setMinElapsed] = useState(minMs === 0);

  useEffect(() => {
    if (minMs <= 0) return;
    const t = setTimeout(() => setMinElapsed(true), minMs);
    return () => clearTimeout(t);
  }, [minMs]);

  return (
    <Suspense fallback={<SuspenseLoader />}>
      <ReadyNotifier onReady={() => setReady(true)}>{children}</ReadyNotifier>
      {(!ready || !minElapsed) && <SuspenseLoader />}
    </Suspense>
  );
};

export default function AppRouter() {
  // NOTE: chunk retry flags are deliberately NOT cleared here. The latch set
  // by lazyRetry must survive the reload it triggers — clearing it on mount
  // made every attempt look like the first one, reloading forever whenever a
  // chunk was permanently missing (the Guardian Management loop). lazyRetry
  // clears its own key on a successful import instead.

  const router = createBrowserRouter([
    {
      element: <PublicLayout />,
      errorElement: <RouteErrorBoundary />,
      children: [
        { path: '/', element: <Navigate to="/login" replace /> },
        {
          path: '/register',
          element: (
            <LazyRoute>
              <Register />
            </LazyRoute>
          ),
        },
        {
          path: '/adminstrationDetails',
          element: (
            <LazyRoute>
              <AdministrationDetails />
            </LazyRoute>
          ),
        },
        {
          path: '/onboarding',
          element: (
            <LazyRoute>
              <Onboarding />
            </LazyRoute>
          ),
        },
        {
          path: '/demoSchedule',
          element: (
            <LazyRoute>
              <DemoSchedule />
            </LazyRoute>
          ),
        },
        {
          path: '/confirmation',
          element: (
            <LazyRoute>
              <Confirmation />
            </LazyRoute>
          ),
        },
        {
          path: '/login',
          element: (
            <LazyRoute>
              <Login />
            </LazyRoute>
          ),
        },
        {
          path: '/forgetpassword',
          element: (
            <LazyRoute>
              <ForgetPassword />
            </LazyRoute>
          ),
        },
        {
          path: '/OtpForgetpassword',
          element: (
            <LazyRoute>
              <OtpSelectionForget />
            </LazyRoute>
          ),
        },
        {
          path: '/NewCredentials',
          element: (
            <LazyRoute>
              <NewCredential />
            </LazyRoute>
          ),
        },
        {
          path: '/confirmOtp',
          element: (
            <LazyRoute>
              <EnterOtp />
            </LazyRoute>
          ),
        },
        {
          path: '/PasswordUpdated',
          element: (
            <LazyRoute>
              <PasswordUpdated />
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.ORG_INVALID_TOKEN,
          element: (
            <LazyRoute>
              <InvalidTokenPage />
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.ORG_ALREADY_CREATED,
          element: (
            <LazyRoute>
              <OnboardingCompletedPage />
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.UNAUTHORIZED,
          element: (
            <LazyRoute>
              <UnauthorizedPage />
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.FIRST_CHANGE_PASSWORD,
          element: (
            <LazyRoute>
              <FirstChangePassword />
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.ORGANIZATION_SELECTION,
          element: (
            <LazyRoute>
              <OrganizationSelection />
            </LazyRoute>
          ),
        },
        {
          // CVA-3748: two-factor code entry. Public branch like the
          // organization-selection page — the user holds only a scoped 2FA token
          // at this point and is not authenticated yet.
          path: APP_ROUTES.TWO_FACTOR_VERIFICATION,
          element: (
            <LazyRoute>
              <TwoFactorVerification />
            </LazyRoute>
          ),
        },
        {
          path: '/email-link',
          element: (
            <LazyRoute>
              <EmailLinkPage />
            </LazyRoute>
          ),
        },
      ],
    },
    {
      element: (
        <ETLGuard>
          <SubscriptionGuard>
            <SessionTimeout>
              <MainLayout />
            </SessionTimeout>
          </SubscriptionGuard>
        </ETLGuard>
      ),
      errorElement: <RouteErrorBoundary />,
      children: [
        {
          path: APP_ROUTES.HOME,
          element: (
            <LazyRoute>
              <Protected>
                <Home />
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.ETL_DASHBOARD,
          element: (
            <LazyRoute>
              <Protected>
                <ETLDashboard />
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.ETL_QUICK_SETUP,
          element: (
            <LazyRoute>
              <Protected>
                <EtlQuickSetup />
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.ETL_QUICK_SETUP_REVIEW,
          element: (
            <LazyRoute>
              <Protected>
                <EtlQuickSetupReview />
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.CHANGE_PASSWORD,
          element: (
            <LazyRoute>
              <Protected>
                <ChangePassword />
              </Protected>
            </LazyRoute>
          ),
        },
        // {
        //   path: APP_ROUTES.CLASSVEEW_AI,
        //   element: (
        //     <LazyRoute>
        //       <ClassveewAIPage />
        //     </LazyRoute>
        //   ),
        // },
        {
          path: APP_ROUTES.STUDENT,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.STUDENT_MANAGEMENT]}>
                <Student />
              </Protected>
            </LazyRoute>
          ),
        },
        {
          /*
           * One route, one screen (CVA-1267). There used to be a /edit and a
           * /view rendering the same component in two modes, both gated by the
           * SAME privilege — so "view" was never a permission boundary, just a
           * second door. The profile now reads and edits, like the teacher one.
           */
          path: `${APP_ROUTES.STUDENT}/:id/view`,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.STUDENT_MANAGEMENT]}>
                <Profile />
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.EVENT_MANAGEMENT,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.EVENT_MANAGEMENT]}>
                <SubscriptionFeatureGuard featureCode="event_creation_level">
                  <Events />
                </SubscriptionFeatureGuard>
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.CREATE_EVENT,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.EVENT_MANAGEMENT]}>
                <SubscriptionFeatureGuard featureCode="event_creation_level">
                  <AddEditEvent />
                </SubscriptionFeatureGuard>
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: `${APP_ROUTES.EVENT_MANAGEMENT}/:eventId/:mode`,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.EVENT_MANAGEMENT]}>
                <SubscriptionFeatureGuard featureCode="event_creation_level">
                  <AddEditEvent />
                </SubscriptionFeatureGuard>
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: `${APP_ROUTES.VIEW_EVENT_CONSENT}/:id`,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.EVENT_MANAGEMENT]}>
                <SubscriptionFeatureGuard featureCode="event_creation_level">
                  <ViewEvents />
                </SubscriptionFeatureGuard>
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.FEE_TEMPLATE,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.FEE_TEMPLATE_MANAGEMENT]}>
                <SubscriptionFeatureGuard featureCode="fee_management">
                  <FeeTemplate />
                </SubscriptionFeatureGuard>
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.BILLING_AND_PLANS,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.BILLING_MANAGEMENT]}>
                <BillingAndPlans />
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.TEACHER_MANAGEMENT,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.TEACHER_MANAGEMENT]}>
                <Teacher />
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.GUARDIAN_ONBOARDING,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.STUDENT_MANAGEMENT]}>
                <GuardianOnboardingPage />
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.CREATE_TEACHER,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.TEACHER_MANAGEMENT]}>
                <CreateTeacher />
              </Protected>
            </LazyRoute>
          ),
        },
        {
          // The active tab is component state, not a URL segment — see
          // pages/teacherManagement/profile/constants.
          path: `${APP_ROUTES.TEACHER_PROFILE}/:id`,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.TEACHER_MANAGEMENT]}>
                <TeacherProfile />
              </Protected>
            </LazyRoute>
          ),
        },
        {
          // Links minted before the create/profile split (CVA-1266) still point
          // at /edit/:id. Redirect rather than 404 them.
          path: `${APP_ROUTES.EDIT_TEACHER}/:id`,
          element: <LegacyEditTeacherRedirect />,
        },
        {
          path: APP_ROUTES.INVOICE_MANAGEMENT,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.INVOICE_MANAGEMENT]}>
                <SubscriptionFeatureGuard featureCode="fee_management">
                  <SessionGuard>
                    <StudentFees />
                  </SessionGuard>
                </SubscriptionFeatureGuard>
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.SESSION_HISTORY,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.VIEW_SESSION_HISTORY]}>
                <SubscriptionFeatureGuard featureCode="fee_management">
                  <SessionHistory />
                </SubscriptionFeatureGuard>
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: '/attendance',
          element: (
            <LazyRoute>
              <Protected
                allowedPrivileges={[
                  APP_FEATURES.ATTENDANCE_MARKING,
                  APP_FEATURES.MY_CLASSROOM_ATTENDANCE_MARKING,
                ]}
              >
                <Attendance />
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.RE_ENROLLMENT_MANAGEMENT,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.RE_ENROLLMENT_MANAGEMENT]}>
                <SubscriptionFeatureGuard featureCode="can_manage_reenrolment">
                  <ReEnrollmentManagement />
                </SubscriptionFeatureGuard>
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.RE_ENROLLMENT_STUDENT_VIEW,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.RE_ENROLLMENT_MANAGEMENT]}>
                <SubscriptionFeatureGuard featureCode="can_manage_reenrolment">
                  <ReEnrollmentStudentList />
                </SubscriptionFeatureGuard>
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.OPERATIONAL_TIME_SETTINGS,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.ATTENDANCE_CUTOFF_MANAGEMENT]}>
                <AttendanceCutoffTime />
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.BOOK_MANAGEMENT,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.BOOK_MANAGEMENT]}>
                <SubscriptionFeatureGuard featureCode="library_management">
                  <BookManagement />
                </SubscriptionFeatureGuard>
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.BOOK_CIRCULATION,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.BOOK_CIRCULATION]}>
                <SubscriptionFeatureGuard featureCode="library_management">
                  <BookCirculation />
                </SubscriptionFeatureGuard>
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.CLASSROOM_MANAGEMENT,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.CLASSROOM_MANAGEMENT]}>
                <ClassRoom />
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.MY_INSTITUTE,
          element: (
            <LazyRoute>
              <Protected>
                <InstituteDetails />
              </Protected>
            </LazyRoute>
          ),
        },
        {
          // CVA-3753: personal profile + security settings. No allowedPrivileges —
          // every authenticated user reaches their own profile. The Institute
          // Configurations tab inside is gated on
          // ORGANIZATION_CONFIGURATION_MANAGEMENT.
          path: APP_ROUTES.MY_PROFILE,
          element: (
            <LazyRoute>
              <Protected>
                <MyProfile />
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.GRADE_MANAGEMENT,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.GRADE_MANAGEMENT]}>
                <Grade />
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.ASSIGN_CLASSROOMS,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.TEACHER_ASSIGNMENT_MANAGEMENT]}>
                <TeacherAssign />
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.SYLLABUS_MANAGEMENT,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.SYLLABUS_MANAGEMENT]}>
                <SubscriptionFeatureGuard featureCode="can_manage_syllabus">
                  <SyllabusManagement />
                </SubscriptionFeatureGuard>
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.ROLE_MANAGEMENT,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.ROLE_MANAGEMENT]}>
                <UserPolicy />
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.CREATE_ROLE,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.ROLE_MANAGEMENT]}>
                <RoleManager mode={SCREEN_MODE.CREATE} />
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: `${APP_ROUTES.EDIT_ROLE}/:id`,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.ROLE_MANAGEMENT]}>
                <RoleManager mode={SCREEN_MODE.EDIT} />
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: `${APP_ROUTES.VIEW_ROLE}/:id`,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.ROLE_MANAGEMENT]}>
                <RoleManager mode={SCREEN_MODE.VIEW} />
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.USER_MANAGEMENT,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.USER_MANAGEMENT]}>
                <AssignUserRole />
              </Protected>
            </LazyRoute>
          ),
        },
        {
          // Now also reachable by a Teacher holding EXAM_MARKING (not just
          // EXAM_MANAGEMENT) — the page itself role-branches its actions
          // (`ScoreCardPage`'s `isExamAdmin` check) and `examActions.fetchExams`
          // already returns a role-scoped result server-side.
          path: APP_ROUTES.EXAM_MANAGEMENT,
          element: (
            <LazyRoute>
              <Protected
                allowedPrivileges={[APP_FEATURES.EXAM_MANAGEMENT, APP_FEATURES.EXAM_MARKING]}
              >
                <SubscriptionFeatureGuard featureCode="exam_management">
                  <ScoreCardPage />
                </SubscriptionFeatureGuard>
              </Protected>
            </LazyRoute>
          ),
        },
        {
          // Same widening as above — a Teacher assigned to a subject (as
          // invigilator or marking teacher) reaches this subject-detail
          // screen too. Row-level scoping happens server side.
          path: `${APP_ROUTES.VIEW_EXAM}/:id`,
          element: (
            <LazyRoute>
              <Protected
                allowedPrivileges={[APP_FEATURES.EXAM_MANAGEMENT, APP_FEATURES.EXAM_MARKING]}
              >
                <SubscriptionFeatureGuard featureCode="exam_management">
                  <ViewExamSubjects />
                </SubscriptionFeatureGuard>
              </Protected>
            </LazyRoute>
          ),
        },
        {
          // Shared marking page. Teachers arrive from Exam Management, Admins
          // from View Exam — `Protected` ORs its privileges (`.some`), so
          // either one grants access. Row-level ownership is enforced server
          // side on every write.
          path: APP_ROUTES.EXAM_SUBJECT_MARKING,
          element: (
            <LazyRoute>
              <Protected
                allowedPrivileges={[APP_FEATURES.EXAM_MARKING, APP_FEATURES.EXAM_MANAGEMENT]}
              >
                <SubscriptionFeatureGuard featureCode="exam_management">
                  <SubjectMarking />
                </SubscriptionFeatureGuard>
              </Protected>
            </LazyRoute>
          ),
        },
        {
          // CVA-4197 plan 0011 slice 4 — the Flow A canvas (per-student,
          // per-question grading), read-only at this slice. Same privilege
          // OR as EXAM_SUBJECT_MARKING above; row-level ownership (marking
          // teacher, owning teacher, or admin) is enforced server side on
          // `GET .../attempts/:studentId`. Reachable only via direct URL for
          // now — the roster's row icon still opens MarkStudentModal
          // unconditionally until slice 7 wires this in.
          path: APP_ROUTES.EXAM_ATTEMPT_MARKING,
          element: (
            <LazyRoute>
              <Protected
                allowedPrivileges={[APP_FEATURES.EXAM_MARKING, APP_FEATURES.EXAM_MANAGEMENT]}
              >
                <SubscriptionFeatureGuard featureCode="exam_management">
                  <MarkExamCanvas />
                </SubscriptionFeatureGuard>
              </Protected>
            </LazyRoute>
          ),
        },
        {
          // CVA-4197 plan 0009 — read-only paper view. Reached from View Exam
          // (Admin) and from the teacher-facing marking screens above, so the
          // guard mirrors EXAM_SUBJECT_MARKING's OR of both privileges. Row-
          // level ownership (assigned teacher or admin) is enforced server
          // side on `GET .../paper`.
          path: APP_ROUTES.EXAM_PAPER_VIEW,
          element: (
            <LazyRoute>
              <Protected
                allowedPrivileges={[APP_FEATURES.EXAM_MANAGEMENT, APP_FEATURES.EXAM_MARKING]}
              >
                <SubscriptionFeatureGuard featureCode="exam_management">
                  <ViewExamPaper />
                </SubscriptionFeatureGuard>
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.HOMEWORK_MANAGEMENT,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.HOMEWORK_MANAGEMENT]}>
                <SubscriptionFeatureGuard featureCode="has_homework_module">
                  <HomeworkDashboard />
                </SubscriptionFeatureGuard>
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.CREATE_HOMEWORK,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.HOMEWORK_MANAGEMENT]}>
                <SubscriptionFeatureGuard featureCode="has_homework_module">
                  <CreateHomework />
                </SubscriptionFeatureGuard>
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.EDIT_HOMEWORK,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.HOMEWORK_MANAGEMENT]}>
                <SubscriptionFeatureGuard featureCode="has_homework_module">
                  <EditHomework />
                </SubscriptionFeatureGuard>
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.VIEW_HOMEWORK,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.HOMEWORK_MANAGEMENT]}>
                <SubscriptionFeatureGuard featureCode="has_homework_module">
                  <ViewHomework />
                </SubscriptionFeatureGuard>
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.HOMEWORK_TASK_GRADING,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.HOMEWORK_MANAGEMENT]}>
                <SubscriptionFeatureGuard featureCode="has_homework_module">
                  <GradingSubmissions />
                </SubscriptionFeatureGuard>
              </Protected>
            </LazyRoute>
          ),
        },
        {
          // CVA-4740 — guardian events, read-only list + calendar.
          path: APP_ROUTES.PARENT_EVENTS,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.PARENT_VIEW_EVENT]}>
                <SubscriptionFeatureGuard featureCode="event_creation_level">
                  <ParentEventsPage />
                </SubscriptionFeatureGuard>
              </Protected>
            </LazyRoute>
          ),
        },
        {
          // CVA-4740 — guardian subjects + syllabus. NOT wrapped in a
          // SubscriptionFeatureGuard: the subject list itself has no
          // subscription gate (the backend marks it @SkipFeatureCheck), and
          // gating the page on the syllabus feature would lock a guardian out
          // of their child's subject list too. The syllabus REQUEST is gated
          // server-side on can_manage_syllabus and surfaces in the panel.
          path: APP_ROUTES.PARENT_SUBJECTS,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.PARENT_VIEW_SUBJECT]}>
                <ParentSubjectsPage />
              </Protected>
            </LazyRoute>
          ),
        },
        {
          // CVA-4740 — guardian timetable. SubscriptionFeatureGuard sends an
          // org without can_manage_timetable to FeatureBlockedPage, matching
          // the guardian Homework and My Exams routes.
          path: APP_ROUTES.PARENT_TIMETABLE,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.PARENT_VIEW_TIMETABLE]}>
                <SubscriptionFeatureGuard featureCode="can_manage_timetable">
                  <ParentTimetablePage />
                </SubscriptionFeatureGuard>
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.PARENT_HOMEWORK_MANAGEMENT,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.PARENT_HOMEWORK]}>
                <SubscriptionFeatureGuard featureCode="has_homework_module">
                  <ParentHomeworkDashboard />
                </SubscriptionFeatureGuard>
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.PARENT_VIEW_HOMEWORK,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.PARENT_HOMEWORK]}>
                <SubscriptionFeatureGuard featureCode="has_homework_module">
                  <ParentViewHomework />
                </SubscriptionFeatureGuard>
              </Protected>
            </LazyRoute>
          ),
        },
        {
          // CVA-4730 — the child's own homework list and detail. Same two
          // components as the /parent/homework routes above ("one flow, two
          // audiences"): the pages resolve WHO is acting through
          // `useStudentPortalIdentity`, and the services pick the
          // student-scoped endpoints by privilege. Separate paths rather than
          // dual-privilege routes (unlike /student/exams) because the two
          // audiences are already separated at the privilege level and each
          // sidebar surfaces only its own pair.
          path: APP_ROUTES.STUDENT_HOMEWORK,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.STUDENT_HOMEWORK]}>
                <SubscriptionFeatureGuard featureCode="has_homework_module">
                  <ParentHomeworkDashboard />
                </SubscriptionFeatureGuard>
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.STUDENT_VIEW_HOMEWORK,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.STUDENT_HOMEWORK]}>
                <SubscriptionFeatureGuard featureCode="has_homework_module">
                  <ParentViewHomework />
                </SubscriptionFeatureGuard>
              </Protected>
            </LazyRoute>
          ),
        },
        {
          // Exam list — shared by a student viewing their own exams and a
          // guardian viewing a child's (CVA-4197 "one flow, two audiences").
          // Dual-privilege, matching STUDENT_EXAM_ATTEMPT below: a guardian's
          // sidebar "Exam Management" entry now routes here too (see
          // constants.ts), superseding the old exam-level-only /parent/exams.
          // Feature-gated like every other exam route; the student's nav
          // entry is additionally hidden when the plan lacks the feature, so
          // a lock screen is never their only destination.
          path: APP_ROUTES.STUDENT_EXAMS,
          element: (
            <LazyRoute>
              <Protected
                allowedPrivileges={[APP_FEATURES.STUDENT_VIEW_EXAM, APP_FEATURES.PARENT_VIEW_EXAM]}
              >
                <SubscriptionFeatureGuard featureCode="exam_management">
                  <StudentExamDashboard />
                </SubscriptionFeatureGuard>
              </Protected>
            </LazyRoute>
          ),
        },
        {
          // `/student/exams/:examId` and `/student/exams/:examId/report-card`
          // differ by a literal segment, so react-router resolves them
          // unambiguously and declaration order is not load-bearing.
          // Dual-privilege for the same reason as the list route above.
          path: APP_ROUTES.STUDENT_EXAM_DETAIL,
          element: (
            <LazyRoute>
              <Protected
                allowedPrivileges={[APP_FEATURES.STUDENT_VIEW_EXAM, APP_FEATURES.PARENT_VIEW_EXAM]}
              >
                <SubscriptionFeatureGuard featureCode="exam_management">
                  <StudentViewExam />
                </SubscriptionFeatureGuard>
              </Protected>
            </LazyRoute>
          ),
        },
        {
          // Dual-privilege for the same reason as the list route above.
          path: APP_ROUTES.STUDENT_EXAM_REPORT_CARD,
          element: (
            <LazyRoute>
              <Protected
                allowedPrivileges={[APP_FEATURES.STUDENT_VIEW_EXAM, APP_FEATURES.PARENT_VIEW_EXAM]}
              >
                <SubscriptionFeatureGuard featureCode="exam_management">
                  <StudentReportCardPage />
                </SubscriptionFeatureGuard>
              </Protected>
            </LazyRoute>
          ),
        },
        {
          // CVA-4812 — a marking teacher (EXAM_MARKING) reaches this from the
          // exam student list; the API scopes them to exams they mark.
          path: APP_ROUTES.EXAM_REPORT_CARD,
          element: (
            <LazyRoute>
              <Protected
                allowedPrivileges={[APP_FEATURES.EXAM_MANAGEMENT, APP_FEATURES.EXAM_MARKING]}
              >
                <SubscriptionFeatureGuard featureCode="exam_management">
                  <ExamReportCard />
                </SubscriptionFeatureGuard>
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.SUBJECT_MANAGEMENT,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.SUBJECT_MANAGEMENT]}>
                <Subjects />
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: '/dashboard',
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.VIEW_DASHBOARD]}>
                <Dashboard />
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.NEWS_FEED,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.VIEW_DASHBOARD]}>
                <NewsFeedPage />
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: `${APP_ROUTES.MANAGE_TIMETABLE}/:id`,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.ROSTER_MANAGEMENT]}>
                <SubscriptionFeatureGuard featureCode="can_manage_timetable">
                  <SetTimeTable />
                </SubscriptionFeatureGuard>
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.TIMETABLE_MANAGEMENT,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.ROSTER_MANAGEMENT]}>
                <SubscriptionFeatureGuard featureCode="can_manage_timetable">
                  <TimeTable />
                </SubscriptionFeatureGuard>
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.CURRICULUM_MANAGEMENT,
          element: (
            <LazyRoute>
              <Protected>
                <CurriculumScreen />
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.ACADEMIC_YEAR_MANAGEMENT,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.ACADEMIC_YEAR_MANAGEMENT]}>
                <SubscriptionFeatureGuard featureCode="academic_year">
                  <AcademicYearPage />
                </SubscriptionFeatureGuard>
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.CREATE_SYLLABUS,
          element: (
            <LazyRoute>
              <Protected>
                <SubscriptionFeatureGuard featureCode="can_manage_syllabus">
                  <CreateSyllabusForm />
                </SubscriptionFeatureGuard>
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: `${APP_ROUTES.EDIT_SYLLABUS}/:id`,
          element: (
            <LazyRoute>
              <Protected>
                <SubscriptionFeatureGuard featureCode="can_manage_syllabus">
                  <CreateSyllabusForm />
                </SubscriptionFeatureGuard>
              </Protected>
            </LazyRoute>
          ),
        },
        {
          // Admin register. It carried a bare <Protected> — no privilege at all —
          // so any signed-in user could open another person's attendance by URL,
          // whatever the sidebar showed.
          path: APP_ROUTES.TEACHER_ATTENDANCE,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.STAFF_ATTENDANCE_MANAGEMENT]}>
                <SubscriptionFeatureGuard featureCode="has_teacher_attendance">
                  <TeacherAttendance />
                </SubscriptionFeatureGuard>
              </Protected>
            </LazyRoute>
          ),
        },
        {
          // The institute's approval queue — other people's leave, so the
          // approver privilege alone opens it.
          path: APP_ROUTES.STAFF_LEAVE,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.STAFF_LEAVE_MANAGEMENT]}>
                <SubscriptionFeatureGuard featureCode="has_teacher_attendance">
                  <StaffLeave scope="ADMIN" />
                </SubscriptionFeatureGuard>
              </Protected>
            </LazyRoute>
          ),
        },
        {
          // The same page in its self-scoped mounting. A separate route rather
          // than a tab, so the nav can put personal leave under Profile and the
          // queue under Staff & Users — and so the server scope can never be
          // widened by a click.
          path: APP_ROUTES.MY_LEAVE,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.SELF_ATTENDANCE_LEAVE]}>
                <SubscriptionFeatureGuard featureCode="has_teacher_attendance">
                  <StaffLeave scope="SELF" />
                </SubscriptionFeatureGuard>
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.GUARDIAN_MANAGEMENT,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.USER_MANAGEMENT]}>
                <GuardianManagementPage />
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.REPORTS,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.REPORT_MANAGEMENT]}>
                <SubscriptionFeatureGuard featureCode="analytics_dashboard">
                  <Reports />
                </SubscriptionFeatureGuard>
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.REPORTS_FINANCE,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.FINANCE_REPORT_MANAGEMENT]}>
                <SubscriptionFeatureGuard featureCode="analytics_dashboard">
                  <FinanceReport />
                </SubscriptionFeatureGuard>
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.REPORTS_STUDENT,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.STUDENT_REPORT_MANAGEMENT]}>
                <SubscriptionFeatureGuard featureCode="analytics_dashboard">
                  <StudentReport />
                </SubscriptionFeatureGuard>
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.REPORTS_TEACHER,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.TEACHER_REPORT_MANAGEMENT]}>
                <SubscriptionFeatureGuard featureCode="analytics_dashboard">
                  <TeacherReport />
                </SubscriptionFeatureGuard>
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.REPORTS_STUDENT_ATTENDANCE,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.STUDENT_ATTENDANCE_REPORT_MANAGEMENT]}>
                <SubscriptionFeatureGuard featureCode="analytics_dashboard">
                  <StudentAttendanceReport />
                </SubscriptionFeatureGuard>
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.ORGANIZATION_ADMIN_MANAGEMENT,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.USER_MANAGEMENT]}>
                <OrganizationAdminManagementPage />
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.ADMIN_ORGANIZATIONS,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.CLASSVEEW_ORGANIZATION_MANAGEMENT]}>
                <ViewAllOrganizationsScreen />
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.ADMIN_REQUEST_ORGANIZATIONS,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.CLASSVEEW_ORGANIZATION_MANAGEMENT]}>
                <RequestOrganizationScreen />
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.ADMIN_ORGANIZATION_CREATE,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.CLASSVEEW_ORGANIZATION_MANAGEMENT]}>
                <CreateOrganizationScreen />
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.ADMIN_ORGANIZATION_VIEW,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.CLASSVEEW_ORGANIZATION_MANAGEMENT]}>
                <ManageOrganizationScreen mode={SCREEN_MODE.VIEW} />
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.ADMIN_ORGANIZATION_EDIT,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.CLASSVEEW_ORGANIZATION_MANAGEMENT]}>
                <ManageOrganizationScreen mode={SCREEN_MODE.EDIT} />
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.ADMIN_STUDENTS,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.CLASSVEEW_STUDENT_MANAGEMENT]}>
                <StudentManagementScreen />
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.ADMIN_STUDENT_VIEW,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.CLASSVEEW_STUDENT_MANAGEMENT]}>
                <ViewEditAdminStudentScreen mode={SCREEN_MODE.VIEW} />
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.ADMIN_STUDENT_EDIT,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.CLASSVEEW_STUDENT_MANAGEMENT]}>
                <ViewEditAdminStudentScreen mode={SCREEN_MODE.EDIT} />
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.ADMIN_TEACHERS,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.CLASSVEEW_TEACHER_MANAGEMENT]}>
                <TeacherManagementScreen />
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.ADMIN_TEACHER_VIEW,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.CLASSVEEW_TEACHER_MANAGEMENT]}>
                <ViewEditAdminTeacherScreen mode={SCREEN_MODE.VIEW} />
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.ADMIN_TEACHER_EDIT,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.CLASSVEEW_TEACHER_MANAGEMENT]}>
                <ViewEditAdminTeacherScreen mode={SCREEN_MODE.EDIT} />
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.ADMIN_GUARDIANS,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.CLASSVEEW_GUARDIAN_MANAGEMENT]}>
                <GuardianManagementScreen />
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.ADMIN_GUARDIAN_VIEW,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.CLASSVEEW_GUARDIAN_MANAGEMENT]}>
                <ViewEditAdminGuardianScreen mode={SCREEN_MODE.VIEW} />
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.ADMIN_GUARDIAN_EDIT,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.CLASSVEEW_GUARDIAN_MANAGEMENT]}>
                <ViewEditAdminGuardianScreen mode={SCREEN_MODE.EDIT} />
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.ADMIN_NEWS_FEED,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.CLASSVEEW_POST_MANAGEMENT]}>
                <AdminNewsFeedScreen />
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.ADMIN_AWARDS,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.CLASSVEEW_AWARD_MANAGEMENT]}>
                <AwardManagementScreen />
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.ADMIN_DISCOUNT_LEDGER,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.CLASSVEEW_DISCOUNT_LEDGER_VIEW]}>
                <DiscountLedgerScreen />
              </Protected>
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.FORBIDDEN,
          element: (
            <LazyRoute>
              <ForbiddenPage />
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.WILD_CARD,
          element: (
            <LazyRoute>
              <NotFoundPage />
            </LazyRoute>
          ),
        },
        {
          path: APP_ROUTES.SUBJECT_ASSIGN,
          element: (
            <LazyRoute>
              <Protected allowedPrivileges={[APP_FEATURES.SUBJECT_MANAGEMENT]}>
                <SubjectAssign />
              </Protected>
            </LazyRoute>
          ),
        },
      ],
    },
    {
      // CVA-4197 join-exam plan 0004 (<<chromeless>>): a THIRD top-level
      // group, sibling to PublicLayout and the MainLayout group above — not
      // a route nested under either. Same guard stack as MainLayout's group
      // minus MainLayout itself, so a student sitting an exam gets no
      // sidebar/navbar but keeps every auth guard.
      element: <ChromelessLayout />,
      errorElement: <RouteErrorBoundary />,
      children: [
        {
          path: APP_ROUTES.STUDENT_EXAM_ATTEMPT,
          element: (
            <LazyRoute>
              <Protected
                allowedPrivileges={[APP_FEATURES.STUDENT_VIEW_EXAM, APP_FEATURES.PARENT_VIEW_EXAM]}
              >
                <SubscriptionFeatureGuard featureCode="exam_management">
                  <StudentExamAttemptPage />
                </SubscriptionFeatureGuard>
              </Protected>
            </LazyRoute>
          ),
        },
      ],
    },
  ]);
  return <RouterProvider router={router} />;
}
